'use client'

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileUp, Loader, BookOpen, FileText, X } from 'lucide-react'
import { extractRawTextFromPDF, generateQuestionsWithAI } from './api'
import { useSelector } from 'react-redux'
import { postTeacherAssignmentFile } from '../postFileAws'


export default function CreateAssignment() {
const navigate = useNavigate()
  const [assignmentTitle, setAssignmentTitle] = useState('')
  const [topic, setTopic] = useState('')
  const [aiPrompt, setAiPrompt] = useState('')
  const [questionCounts, setQuestionCounts] = useState({
    essay: 0,
    shortAnswer: 0,
    MCQ: 0,
  })
  const [selectedDifficulties, setSelectedDifficulties] = useState(['medium'])
  const [pdfFile, setPdfFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [assignmentData, setAssignmentData] = useState(null)
  const [selectedClass, setSelectedClass] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const fileInputRef = useRef(null)
  const classCode = useSelector((state) => state.teacherSub.classCode);
  const classValue=localStorage.getItem("class");

  const questionTypes = [
    { id: 'essay', label: 'Essay Type', icon: FileText },
    { id: 'shortAnswer', label: 'Short Answer', icon: BookOpen },
    { id: 'MCQ', label: 'MCQ', icon: BookOpen },
  ]

  const difficultyLevels = [
    { id: 'easy', label: 'Easy' },
    { id: 'medium', label: 'Medium' },
    { id: 'hard', label: 'Hard' }
  ]

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (file && file.type === 'application/pdf') {
         setPdfFile(file)
   
         const teacherId = localStorage.getItem("teacherId")
   
         const response = await postTeacherAssignmentFile(file, teacherId, assignmentData.assignment_topic, 'refMaterial' );
   
         console.log(response)
         const updatedAssignmentData = {
           ...assignmentData,
           refMaterial: response.uri,
           referenceType: 'pdf'
         }
   
         setAssignmentData(updatedAssignmentData);
   
         localStorage.setItem('assignmentData', JSON.stringify(updatedAssignmentData))
         setError('')
   
       } else if (file) {
         setPdfFile(file)
   
         const teacherId = localStorage.getItem("teacherId")
   
         const response = await postTeacherAssignmentFile(file, teacherId, assignmentData.assignment_topic, 'refMaterial' )
   
         const updatedAssignmentData = {
           ...assignmentData,
           refMaterial: response.url,
           referenceType: 'image'
         }
   
         setAssignmentData(updatedAssignmentData);
   
         localStorage.setItem('assignmentData', JSON.stringify(updatedAssignmentData))
         setError('')
       }else {
         setError('Please upload a file')
         setPdfFile(null)
       }
  }

   useEffect(() => {
      const storedAssignmentData = localStorage.getItem('assignmentData');
      if (storedAssignmentData) {
        const parsedData = JSON.parse(storedAssignmentData);
        setAssignmentData(parsedData);
        setAssignmentTitle(parsedData.assignment_topic);
      }
  
      // Load selected class from localStorage
      const savedClass = localStorage.getItem('class');
      if (savedClass) {
        setSelectedClass(savedClass);
      }
    }, []);

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    
    // Create the object with the required information
    const assignmentInfo = {
      topic,
      mcqNumber: questionCounts.MCQ,
      shortAnswerNumber: questionCounts.shortAnswer,
      essayNumber: questionCounts.essay,
      questionDifficulty: selectedDifficulties,
      referenceType: pdfFile ? pdfFile.type : null,
      refMaterial: pdfFile ? pdfFile.name : null,
      userPrompt: aiPrompt,
    }

    // Console log the object
    console.log(assignmentInfo)
    
    // Calculate total questions
    const totalQuestions = Object.values(questionCounts).reduce((sum, count) => sum + count, 0)

    
        try {
    
          console.log('Generating questions with AI')
      
          const result = await generateQuestionsWithAI({
            topic,
            mcqNumber: questionCounts.MCQ,
            shortAnswerNumber: questionCounts.shortAnswer,
            essayNumber: questionCounts.essay,
            questionDifficulty: selectedDifficulties,
            referenceType: assignmentData.referenceType || (pdfFile ? pdfFile.type : null),
            refMaterial: assignmentData.refMaterial || (pdfFile ? pdfFile.name : null),
            userPrompt: aiPrompt,
            classValue:classValue
          })
    
          console.log('AI generation result:', result)
          
          if (result.length) {
            console.log('Questions generated successfully')
            const updatedAssignmentData = {
              ...assignmentData,
              assignmentQuestion: result,
              classCode: classCode
            }
            localStorage.setItem('assignmentData', JSON.stringify(updatedAssignmentData))
            setSuccess('Questions added to assignment successfully!')
            
            console.log('Attempting navigation')
            navigate('/assignments/createWithAI/AiGeneratedAssignment', { state: { assignment: updatedAssignmentData } })
          } else {
            console.error('Failed to generate questions:', result)
            setError('Failed to add questions to assignment. Please try again.')
          }
    
        } catch (err) {
          console.error('Error in handleSubmit:', err)
          setError(err.message || 'Failed to add questions to assignment')
        } finally {
          setIsLoading(false)
          console.log('handleSubmit completed')
        }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8">
        <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">
          Create AI-Powered Assignment
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="assignmentTitle" className="block mb-2 font-medium text-gray-700">
                Assignment Title
              </label>
              <input
                id="assignmentTitle"
                type="text"
                value={assignmentTitle}
                onChange={(e) => setAssignmentTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter assignment title"
                required
              />
            </div>
            <div>
              <label htmlFor="topic" className="block mb-2 font-medium text-gray-700">
                Topic
              </label>
              <input
                id="topic"
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter the assignment topic"
                required
              />
            </div>
          </div>

          <div className="col-span-full">
            <label htmlFor="aiPrompt" className="block mb-2 font-medium text-gray-700">
              AI Prompt
            </label>
            <textarea
              id="aiPrompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter the prompt for AI"
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Reference Material (Optional)
              </label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <FileUp className="w-5 h-5" />
                  Attach File
                </button>
                {pdfFile && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {pdfFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPdfFile(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf, image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Difficulty Level
              </label>
              <div className="grid grid-cols-3 gap-2">
                {difficultyLevels.map((level) => (
                  <label
                    key={level.id}
                    className={`flex items-center justify-center p-2 border rounded-lg cursor-pointer transition-colors ${
                      selectedDifficulties.includes(level.id)
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDifficulties.includes(level.id)}
                      onChange={(e) => {
                        setSelectedDifficulties(prev =>
                          e.target.checked
                            ? [...prev, level.id]
                            : prev.filter(d => d !== level.id)
                        )
                      }}
                      className="hidden"
                    />
                    <span className="text-sm">{level.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-2 font-medium text-gray-700">
              Question Types
            </label>
            <div className="flex justify-between items-stretch">
              {questionTypes.map((type) => (
                <div key={type.id} className="flex-1 flex items-center border border-gray-300 rounded-lg overflow-hidden mx-1">
                  <div className="flex-1 flex items-center justify-center gap-2 bg-gray-50 px-3 py-2">
                    <type.icon className="w-5 h-5 text-gray-600" />
                    <span className="whitespace-nowrap text-sm font-medium text-gray-700">{type.label}</span>
                  </div>
                  <div className="px-2 py-1">
                    <input
                      type="number"
                      min="0"
                      value={questionCounts[type.id]}
                      onChange={(e) => setQuestionCounts(prev => ({
                        ...prev,
                        [type.id]: parseInt(e.target.value) || 0
                      }))}
                      className="w-16 p-1 text-center border-0 focus:ring-0 focus:outline-none"
                      placeholder="0"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            className={`w-full py-3 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 ${
              isLoading || !assignmentTitle || !topic || !aiPrompt || Object.values(questionCounts).every(count => count === 0) || selectedDifficulties.length === 0
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Creating Assignment...
              </>
            ) : (
              'Create Assignment'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}